/**
 * Metric Card Component
 * Display key metrics with labels, values, and trends
 */

import { HTMLAttributes } from 'react'
import { cn, formatNumber, formatPercent } from '@/lib/utils'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  title: string
  value: string | number
  change?: number
  changeType?: 'absolute' | 'percent'
  icon?: React.ReactNode
  trend?: 'up' | 'down' | 'neutral'
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'success' | 'warning' | 'danger'
}

export function MetricCard({
  title,
  value,
  change,
  changeType = 'percent',
  icon,
  trend,
  size = 'md',
  variant = 'default',
  className,
  ...props
}: MetricCardProps) {
  const sizeStyles = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  }

  const valueSizeStyles = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl',
  }

  // Auto-detect trend from change if not provided
  const detectedTrend = trend || (change === undefined ? 'neutral' : change > 0 ? 'up' : change < 0 ? 'down' : 'neutral')

  const TrendIcon = detectedTrend === 'up' ? TrendingUp : detectedTrend === 'down' ? TrendingDown : Minus

  const trendColor = detectedTrend === 'up' ? 'text-success' : detectedTrend === 'down' ? 'text-destructive' : 'text-muted-foreground'

  return (
    <div
      className={cn(
        'card rounded-xl border p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg',
        sizeStyles[size],
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className={cn('mt-2 font-bold tracking-tight', valueSizeStyles[size], 'text-foreground')}>
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {change !== undefined && (
            <div className={cn('mt-2 flex items-center text-sm', trendColor)}>
              <TrendIcon className="mr-1 h-4 w-4" />
              <span>
                {changeType === 'percent' ? formatPercent(change) : formatNumber(change)}
              </span>
              <span className="ml-1 text-muted-foreground">vs last period</span>
            </div>
          )}
        </div>
        {icon && (
          <div className="ml-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {icon}
          </div>
        )}
      </div>
    </div>
  )
}
